import { useEffect, useState, useMemo } from "react";
import { Package, Plus, Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProducts, ProductWithCategory } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { ProductFormDialog } from "@/components/products/ProductFormDialog";
import { ProductList } from "@/components/products/ProductList";
import { ProductRecipeDialog } from "@/components/products/ProductRecipeDialog";

export default function ProdutosPage() {
  const {
    products,
    isLoading,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    toggleActive,
  } = useProducts();

  const { categories, fetchCategories } = useCategories();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductWithCategory | null>(null);
  const [recipeProduct, setRecipeProduct] = useState<ProductWithCategory | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory =
        categoryFilter === "all" ||
        (categoryFilter === "none" && !product.category_id) ||
        product.category_id === categoryFilter;

      // Status filter
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && product.is_active) ||
        (statusFilter === "inactive" && !product.is_active);

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [products, searchQuery, categoryFilter, statusFilter]);

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (product: ProductWithCategory) => {
    setEditingProduct(product);
    setDialogOpen(true);
  };

  const handleSubmit = async (
    data: {
      name: string;
      description?: string;
      price: number;
      category_id?: string | null;
      is_active: boolean;
    },
    imageFile?: File
  ) => {
    setIsSubmitting(true);

    if (editingProduct) {
      await updateProduct(editingProduct.id, data, imageFile);
    } else {
      await createProduct(data, imageFile);
    }

    setIsSubmitting(false);
    setDialogOpen(false);
    setEditingProduct(null);
  };

  const handleToggleActive = async (product: ProductWithCategory) => {
    await toggleActive(product);
  };

  const handleDelete = async (id: string) => {
    await deleteProduct(id);
  };

  const handleManageRecipe = (product: ProductWithCategory) => {
    setRecipeProduct(product);
    setRecipeDialogOpen(true);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setCategoryFilter("all");
    setStatusFilter("all");
  };

  const hasActiveFilters =
    searchQuery !== "" || categoryFilter !== "all" || statusFilter !== "all";

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand/10 flex items-center justify-center">
            <Package className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Produtos</h1>
            <p className="text-sm text-muted-foreground">
              Gerencie os produtos do seu cardápio
            </p>
          </div>
        </div>

        <Button onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas categorias</SelectItem>
            <SelectItem value="none">Sem categoria</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Limpar filtros
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Package className="h-16 w-16 text-muted-foreground/30 mb-4" />
          <h2 className="text-lg font-medium text-foreground mb-2">
            Nenhum produto cadastrado
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mb-4">
            Adicione produtos ao seu cardápio para começar a vender.
          </p>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Criar primeiro produto
          </Button>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Filter className="h-12 w-12 text-muted-foreground/30 mb-4" />
          <h2 className="text-lg font-medium text-foreground mb-2">
            Nenhum produto encontrado
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mb-4">
            Tente ajustar os filtros de busca.
          </p>
          <Button variant="outline" onClick={clearFilters}>
            Limpar filtros
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-3">
            {filteredProducts.length} de {products.length} produto
            {products.length !== 1 ? "s" : ""}
          </p>
          <ProductList
            products={filteredProducts}
            onEdit={handleOpenEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onManageRecipe={handleManageRecipe}
          />
        </>
      )}

      <ProductFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        product={editingProduct}
        categories={categories}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />

      <ProductRecipeDialog
        open={recipeDialogOpen}
        onOpenChange={setRecipeDialogOpen}
        product={recipeProduct}
      />
    </div>
  );
}
